/**
 * Message Input Component
 * Gemini-inspired compound input: text area + toolbar row
 * Toolbar: [+] [Diagnostic] [Analyser] ... [Send]
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Keyboard,
  Platform,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { validateMessage } from '../../utils/validators';
import { MESSAGE_LIMITS } from '../../utils/constants';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';

interface MessageInputProps {
  onSend: (message: string) => void;
  onDiagnosticPress?: () => void;
  onAnalysePress?: () => void;
  analyseLabel?: string;
  analyseIcon?: string;
  onScannerPress?: () => void;
  scannerLabel?: string;
  scannerIcon?: string;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
}

// Calculate line height (font size + padding)
const LINE_HEIGHT = typography.fontSize.md * 1.4;
const MIN_HEIGHT = 40; // Fixed minimal height for 1 line
const MAX_LINES = 6;
const MAX_HEIGHT = LINE_HEIGHT * MAX_LINES + spacing.sm * 2;

export default function MessageInput({
  onSend,
  onDiagnosticPress,
  onAnalysePress,
  analyseLabel = 'Analyser',
  analyseIcon = 'chart-bar',
  onScannerPress,
  scannerLabel = 'Scanner de dépenses',
  scannerIcon = 'magnify-scan',
  disabled = false,
  loading = false,
  placeholder: customPlaceholder,
}: MessageInputProps) {
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [inputHeight, setInputHeight] = useState(MIN_HEIGHT);

  const handleSend = () => {
    const validation = validateMessage(message);

    if (!validation.valid) {
      setError(validation.error || 'Message invalide');
      return;
    }

    setError(null);
    onSend(message.trim());
    setMessage('');
    setInputHeight(MIN_HEIGHT); // Reset height after sending
    Keyboard.dismiss();
  };

  const handleKeyPress = (e: any) => {
    if (Platform.OS === 'web' && e.nativeEvent.key === 'Enter' && !e.nativeEvent.shiftKey) {
      e.preventDefault();
      if (message.trim() && !disabled && !loading) {
        handleSend();
      }
    }
  };

  const handleContentSizeChange = (event: any) => {
    // Get the content height from the event
    const contentHeight = event.nativeEvent.contentSize.height;

    // If message is empty, reset to minimum height
    if (message.trim().length === 0) {
      setInputHeight(MIN_HEIGHT);
      return;
    }

    // Clamp between MIN_HEIGHT and MAX_HEIGHT
    const clampedHeight = Math.max(MIN_HEIGHT, Math.min(contentHeight, MAX_HEIGHT));

    setInputHeight(clampedHeight);
  };

  const characterCount = message.length;
  const isNearLimit = characterCount > MESSAGE_LIMITS.MAX_LENGTH * 0.9;

  return (
    <View style={styles.container}>
      {error && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}
      <View style={styles.inputCard}>
        {/* Text input area */}
        <View style={styles.inputRow}>
          <TextInput
            style={[styles.input, { height: inputHeight }]}
            value={message}
            onChangeText={(text) => {
              setMessage(text);
              setError(null);
            }}
            onContentSizeChange={handleContentSizeChange}
            placeholder={customPlaceholder || "Posez une question sur vos finances..."}
            placeholderTextColor={colors.textSecondary}
            multiline
            maxLength={MESSAGE_LIMITS.MAX_LENGTH}
            editable={!disabled && !loading}
            textAlignVertical="top"
            blurOnSubmit={false}
            onKeyPress={handleKeyPress}
          />
        </View>

        {/* Toolbar row */}
        <View style={styles.toolbar}>
          <View style={styles.toolbarLeft}>
            {/* Diagnostic pill button */}
            {onDiagnosticPress && (
              <TouchableOpacity
                style={styles.pillButton}
                onPress={onDiagnosticPress}
                disabled={disabled || loading}
              >
                <Icon name="stethoscope" size={14} color={colors.primary} />
                <Text style={styles.pillButtonText}>Diagnostic</Text>
              </TouchableOpacity>
            )}

            {/* Analyse pill button */}
            {onAnalysePress && (
              <TouchableOpacity
                style={[styles.pillButton, styles.pillButtonAccent]}
                onPress={onAnalysePress}
                disabled={disabled || loading}
              >
                <Icon name={analyseIcon} size={14} color={colors.primary} />
                <Text style={styles.pillButtonText}>{analyseLabel}</Text>
              </TouchableOpacity>
            )}

            {/* Scanner pill button (item 11) */}
            {onScannerPress && (
              <TouchableOpacity
                style={[styles.pillButton, styles.pillButtonAccent]}
                onPress={onScannerPress}
                disabled={disabled || loading}
              >
                <Icon name={scannerIcon} size={14} color={colors.primary} />
                <Text style={styles.pillButtonText}>{scannerLabel}</Text>
              </TouchableOpacity>
            )}
          </View>

          {/* Send button */}
          <TouchableOpacity
            style={[
              styles.sendButton,
              (!message.trim() || disabled || loading) && styles.sendButtonDisabled,
            ]}
            onPress={handleSend}
            disabled={!message.trim() || disabled || loading}
          >
            <Icon
              name="send"
              size={18}
              color="#FFFFFF"
            />
          </TouchableOpacity>
        </View>
      </View>
      {isNearLimit && (
        <Text style={styles.characterCount}>
          {characterCount} / {MESSAGE_LIMITS.MAX_LENGTH}
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.background,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    padding: spacing.md,
  },
  errorContainer: {
    backgroundColor: '#FFF3F3',
    padding: spacing.sm,
    borderRadius: 8,
    marginBottom: spacing.sm,
  },
  errorText: {
    fontSize: typography.fontSize.xs,
    color: colors.error,
  },
  inputCard: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.border,
    overflow: 'hidden',
  },
  inputRow: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
  },
  input: {
    flex: 1,
    fontSize: typography.fontSize.md,
    lineHeight: LINE_HEIGHT,
    color: colors.text,
    paddingVertical: spacing.xs,
    paddingHorizontal: 0,
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.sm,
    paddingBottom: spacing.sm,
    paddingTop: spacing.xs,
  },
  toolbarLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flex: 1,
  },
  pillButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 16,
    backgroundColor: colors.background,
    borderWidth: 1,
    borderColor: colors.border,
  },
  pillButtonAccent: {
    // Same style but can be differentiated later
  },
  pillButtonText: {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold,
    color: colors.primary,
  },
  sendButton: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: spacing.sm,
  },
  sendButtonDisabled: {
    backgroundColor: colors.textTertiary,
  },
  characterCount: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    textAlign: 'right',
    marginTop: spacing.xs,
  },
});
